// frontend/src/components/layout/Header.js
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { useNavigate, Link } from 'react-router-dom';
import {
    Menu, Bell, User, Settings, LogOut, Clock, Plus, FileText,
    ChevronDown, Home, Search, HelpCircle, UserCheck, Mail, ChevronRight,
    CheckCircle, AlertCircle, Info, Loader2, BellRing, BellOff, X,
} from 'lucide-react';
import mayfairLogo from '../../logo.svg';
import api from '../../services/api';

// =========================================================
// CONSTANTS
// =========================================================
const NOTIF_STYLES = {
    success: { bg: 'bg-green-500/15', text: 'text-green-600 dark:text-green-400', dot: 'bg-green-500', Icon: CheckCircle },
    warning: { bg: 'bg-amber-500/15', text: 'text-amber-600 dark:text-amber-400', dot: 'bg-amber-500', Icon: AlertCircle },
    error: { bg: 'bg-red-500/15', text: 'text-red-600 dark:text-red-400', dot: 'bg-red-500', Icon: AlertCircle },
    info: { bg: 'bg-blue-500/15', text: 'text-blue-600 dark:text-blue-400', dot: 'bg-blue-500', Icon: Info },
};

const NOTIF_LIMITS = {
    MAX_KEEP: 50,
    MAX_DISPLAY: 15,
    POLL_INTERVAL: 120_000,
    REQUEST_DEBOUNCE: 5_000,
    RATE_LIMIT_BACKOFF: 60_000,
};

const WS_LIMITS = {
    MAX_RECONNECT: 5,
    INITIAL_DELAY: 3_000,
    MAX_DELAY: 15_000,
    BACKOFF_FACTOR: 1.5,
    CONNECT_TIMEOUT: 8_000,
};

// =========================================================
// HOOKS
// =========================================================
function useClock() {
    const [time, setTime] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setTime(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return time.toLocaleTimeString('en-KE', {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
}

function useClickOutside(refs, handler) {
    useEffect(() => {
        const onDown = (e) => {
            const hitInside = refs.some((r) => r.current && r.current.contains(e.target));
            if (!hitInside) handler();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [refs, handler]);
}

function usePersistedState(key, initial) {
    const [value, setValue] = useState(() => {
        try {
            const saved = localStorage.getItem(key);
            return saved === null ? initial : JSON.parse(saved);
        } catch {
            return initial;
        }
    });
    useEffect(() => {
        try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
    }, [key, value]);
    return [value, setValue];
}

// =========================================================
// HELPERS
// =========================================================
function getGreeting() {
    const h = new Date().getHours();
    if (h < 12) return { text: 'Good Morning', emoji: '🌅' };
    if (h < 17) return { text: 'Good Afternoon', emoji: '☀️' };
    return { text: 'Good Evening', emoji: '🌙' };
}

function formatRelativeTime(ts) {
    if (!ts) return 'Just now';
    const diff = Date.now() - new Date(ts).getTime();
    if (diff < 60_000) return 'Just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 172_800_000) return 'Yesterday';
    return new Date(ts).toLocaleDateString();
}

function getNotificationStyle(type) {
    return NOTIF_STYLES[type] || NOTIF_STYLES.info;
}

function groupNotificationsByDay(items) {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);

    const buckets = { Today: [], Yesterday: [], Earlier: [] };
    for (const n of items) {
        const d = new Date(n.time);
        if (d >= today) buckets.Today.push(n);
        else if (d >= yesterday) buckets.Yesterday.push(n);
        else buckets.Earlier.push(n);
    }
    return buckets;
}

// ─────────────────────────────────────────────────────────
// Build the WebSocket URL
//   • If REACT_APP_WS_URL is set to an absolute ws:// or wss:// URL → use it
//   • Otherwise → same-origin wss://<current host>/ws  (production)
// ─────────────────────────────────────────────────────────
function buildWebSocketUrl(token) {
    const override = process.env.REACT_APP_WS_URL;

    let base;
    if (override && /^wss?:\/\//i.test(override)) {
        base = override.replace(/\/+$/, ''); // strip trailing slashes
    } else {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        base = `${proto}//${window.location.host}/ws`;
    }

    const sep = base.includes('?') ? '&' : '?';
    return `${base}${sep}token=${encodeURIComponent(token)}`;
}

// =========================================================
// MAIN COMPONENT
// =========================================================
export default function Header({ onToggleSidebar, isMobile }) {
    const { user } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // ---------- UI state ----------
    const [profileOpen, setProfileOpen] = useState(false);
    const [notifOpen, setNotifOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);

    // ---------- Notifications ----------
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notificationSound, setNotificationSound] = usePersistedState('notificationSound', true);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationIds, setNotificationIds] = useState(new Set());
    const [error, setError] = useState(null);
    const [isRateLimited, setIsRateLimited] = useState(false);
    const [lastRequestTime, setLastRequestTime] = useState(0);

    // ---------- Realtime ----------
    const [wsConnected, setWsConnected] = useState(null); // null = connecting, true = live, false = offline
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [shouldReconnect, setShouldReconnect] = useState(true);

    // ---------- Refs ----------
    const profileRef = useRef(null);
    const notifRef = useRef(null);
    const searchRef = useRef(null);
    const wsRef = useRef(null);
    const pollIntervalRef = useRef(null);
    const audioRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectDelayRef = useRef(WS_LIMITS.INITIAL_DELAY);
    const rateLimitTimeoutRef = useRef(null);
    const fetchTimeoutRef = useRef(null);
    const isMountedRef = useRef(true);
    const isConnectingRef = useRef(false);
    const connectionIdRef = useRef(0);

    // ---------- Memoized ----------
    const greeting = useMemo(() => getGreeting(), []);
    const clock = useClock();

    const initials = useMemo(() => (
        user?.full_name?.split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2) || 'U'
    ), [user]);

    const displayNotifications = useMemo(
        () => notifications.slice(0, NOTIF_LIMITS.MAX_DISPLAY),
        [notifications]
    );

    const grouped = useMemo(() => groupNotificationsByDay(displayNotifications), [displayNotifications]);

    // ---------- Click outside ----------
    const closeAll = useCallback(() => {
        setProfileOpen(false);
        setNotifOpen(false);
        setSearchOpen(false);
    }, []);
    useClickOutside([profileRef, notifRef, searchRef], closeAll);

    // ---------- Keyboard shortcuts ----------
    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setSearchOpen(true);
            }
            if (e.key === 'Escape') closeAll();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [closeAll]);

    // ---------- Audio unlock ----------
    useEffect(() => {
        audioRef.current = new Audio('/notification.mp3');
        audioRef.current.volume = 0.5;

        const unlock = () => {
            if (audioRef.current) {
                audioRef.current.play().then(() => audioRef.current.pause()).catch(() => { });
            }
            document.removeEventListener('click', unlock);
        };
        document.addEventListener('click', unlock);

        return () => {
            document.removeEventListener('click', unlock);
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // ---------- Browser permission ----------
    useEffect(() => {
        if (!('Notification' in window)) return;
        if (Notification.permission !== 'default') return;
        const t = setTimeout(() => Notification.requestPermission(), 5_000);
        return () => clearTimeout(t);
    }, []);

    // ---------- Play sound ----------
    const playSound = useCallback(() => {
        if (notificationSound && audioRef.current) {
            audioRef.current.play().catch(() => { });
        }
    }, [notificationSound]);

    // ---------- Add notification ----------
    const addNotification = useCallback((n) => {
        if (!n?.id || notificationIds.has(n.id)) return;

        const enriched = {
            id: n.id,
            title: n.title || 'New Notification',
            message: n.message || n.body || '',
            time: n.time || n.created_at || new Date().toISOString(),
            type: n.type || 'info',
            read: n.is_read || false,
            link: n.link || null,
        };

        setNotificationIds((prev) => new Set(prev).add(enriched.id));
        setNotifications((prev) => [enriched, ...prev].slice(0, NOTIF_LIMITS.MAX_KEEP));

        if (!enriched.read) {
            setUnreadCount((c) => c + 1);
            playSound();
        }

        if ('Notification' in window && Notification.permission === 'granted' && !enriched.read) {
            try {
                new Notification(enriched.title, { body: enriched.message, icon: '/favicon.ico' });
            } catch { /* ignore */ }
        }
    }, [notificationIds, playSound]);

    // ---------- Fetch notifications ----------
    const fetchNotifications = useCallback(async (force = false) => {
        if (!isMountedRef.current) return;
        if (!force && isRateLimited) return;

        const now = Date.now();
        if (!force && now - lastRequestTime < NOTIF_LIMITS.REQUEST_DEBOUNCE) return;

        setLastRequestTime(now);

        try {
            const res = await api.get(`/notifications?limit=${NOTIF_LIMITS.MAX_KEEP}`);
            const data = res.data?.data || res.data || [];

            if (!isMountedRef.current) return;

            const ids = new Set();
            let unread = 0;
            for (const n of data) {
                ids.add(n.id);
                if (!n.is_read) unread++;
            }

            setNotificationIds(ids);
            setNotifications(data);
            setUnreadCount(unread);
            setLoading(false);
            setError(null);
            setIsRateLimited(false);

            if (rateLimitTimeoutRef.current) {
                clearTimeout(rateLimitTimeoutRef.current);
                rateLimitTimeoutRef.current = null;
            }
        } catch (err) {
            if (!isMountedRef.current) return;

            if (err.response?.status === 429) {
                setIsRateLimited(true);
                setError('Too many requests. Please wait a moment.');

                if (rateLimitTimeoutRef.current) clearTimeout(rateLimitTimeoutRef.current);
                rateLimitTimeoutRef.current = setTimeout(() => {
                    if (isMountedRef.current) {
                        setIsRateLimited(false);
                        setError(null);
                        fetchNotifications(true);
                    }
                }, NOTIF_LIMITS.RATE_LIMIT_BACKOFF);
            } else {
                console.error('[Header] Notification fetch failed:', err.message);
                setError('Failed to load notifications');
                setLoading(false);
            }
        }
    }, [isRateLimited, lastRequestTime]);

    // ---------- Mark read ----------
    const markAsRead = useCallback(async (id) => {
        try {
            await api.put(`/notifications/${id}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n.id === id ? { ...n, read: true, is_read: true } : n))
            );
            setUnreadCount((c) => Math.max(0, c - 1));
        } catch (err) {
            console.error('[Header] Mark read failed:', err.message);
        }
    }, []);

    const markAllAsRead = useCallback(async () => {
        try {
            await api.put('/notifications/read-all');
            setNotifications((prev) => prev.map((n) => ({ ...n, read: true, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('[Header] Mark all read failed:', err.message);
        }
    }, []);

    // ---------- WebSocket ----------
    const connectWebSocket = useCallback(() => {
        if (isConnectingRef.current) return;
        if (!isMountedRef.current) return;
        if (reconnectAttempts >= WS_LIMITS.MAX_RECONNECT || !shouldReconnect) {
            setWsConnected(false);
            return;
        }

        const token = localStorage.getItem('token');
        if (!token) {
            setWsConnected(false);
            return;
        }

        // Close existing connection
        if (wsRef.current) {
            try {
                const ws = wsRef.current;
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.onclose = null;
                    ws.close(1000, 'Reconnecting');
                }
            } catch { /* ignore */ }
            wsRef.current = null;
        }

        try {
            isConnectingRef.current = true;
            const connId = ++connectionIdRef.current;

            // ── Fixed: build the WS URL from window.location, not from REACT_APP_API_URL ──
            const wsUrl = buildWebSocketUrl(token);

            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            const timeout = setTimeout(() => {
                if (ws.readyState === WebSocket.CONNECTING) ws.close();
                if (isMountedRef.current && wsRef.current === ws) {
                    setWsConnected(false);
                    isConnectingRef.current = false;
                }
            }, WS_LIMITS.CONNECT_TIMEOUT);

            ws.onopen = () => {
                clearTimeout(timeout);
                isConnectingRef.current = false;
                if (!isMountedRef.current || wsRef.current !== ws) return;

                setWsConnected(true);
                setError(null);
                setReconnectAttempts(0);
                reconnectDelayRef.current = WS_LIMITS.INITIAL_DELAY;

                // Once WS is live, refetch notifications so we don't miss anything
                // that was pushed while we were offline.
                fetchNotifications(true);

                try {
                    ws.send(JSON.stringify({ type: 'subscribe', channels: ['notifications'] }));
                } catch { /* ignore */ }
            };

            ws.onmessage = (event) => {
                if (!isMountedRef.current || wsRef.current !== ws) return;
                try {
                    const data = JSON.parse(event.data);
                    if (data.type === 'notification' && data.notification) {
                        addNotification(data.notification);
                    } else if (data.type === 'initial_count') {
                        setUnreadCount(data.unreadCount || 0);
                    }
                } catch (err) {
                    console.error('[Header] WS parse error:', err);
                }
            };

            ws.onclose = (event) => {
                clearTimeout(timeout);
                isConnectingRef.current = false;
                if (wsRef.current !== ws) return;
                if (!isMountedRef.current) return;

                setWsConnected(false);

                // Clean close from our side → do NOT reconnect
                if (event.code === 1000) return;

                if (reconnectAttempts >= WS_LIMITS.MAX_RECONNECT || !shouldReconnect) return;

                const delay = Math.min(reconnectDelayRef.current, WS_LIMITS.MAX_DELAY);
                if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (isMountedRef.current && shouldReconnect && wsRef.current === ws) {
                        setReconnectAttempts((p) => p + 1);
                        reconnectDelayRef.current *= WS_LIMITS.BACKOFF_FACTOR;
                        connectWebSocket();
                    }
                }, delay);
            };

            ws.onerror = () => { /* handled in onclose */ };
        } catch (err) {
            isConnectingRef.current = false;
            if (isMountedRef.current) setWsConnected(false);
            console.error('[Header] WS connect error:', err);
        }
    }, [reconnectAttempts, shouldReconnect, addNotification, fetchNotifications]);

    // ---------- Polling fallback ----------
    useEffect(() => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = setInterval(() => {
            if (wsConnected || isRateLimited || !isMountedRef.current) return;
            if (Date.now() - lastRequestTime < NOTIF_LIMITS.REQUEST_DEBOUNCE) return;
            fetchNotifications(true);
        }, NOTIF_LIMITS.POLL_INTERVAL);

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        };
    }, [wsConnected, isRateLimited, lastRequestTime, fetchNotifications]);

    // ---------- Mount / unmount ----------
    useEffect(() => {
        isMountedRef.current = true;

        fetchTimeoutRef.current = setTimeout(() => fetchNotifications(true), 2000);
        const wsTimer = setTimeout(() => connectWebSocket(), 1000);

        return () => {
            isMountedRef.current = false;
            clearTimeout(fetchTimeoutRef.current);
            clearTimeout(wsTimer);

            if (wsRef.current) {
                try {
                    const ws = wsRef.current;
                    if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                        ws.onclose = null;
                        ws.close(1000, 'Unmounting');
                    }
                } catch { /* ignore */ }
                wsRef.current = null;
            }

            isConnectingRef.current = false;

            if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            if (rateLimitTimeoutRef.current) clearTimeout(rateLimitTimeoutRef.current);
        };
    }, []); // eslint-disable-line

    // ---------- Handlers ----------
    const handleLogout = () => {
        setShouldReconnect(false);
        isConnectingRef.current = false;

        if (wsRef.current) {
            try {
                const ws = wsRef.current;
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.onclose = null;
                    ws.close(1000, 'Logout');
                }
            } catch { /* ignore */ }
            wsRef.current = null;
        }

        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);

        dispatch(logout());
        navigate('/login');
    };

    const handleNotifClick = (n) => {
        if (!n.read && !n.is_read) markAsRead(n.id);
        if (n.link) navigate(n.link);
        setNotifOpen(false);
    };

    const handleDismissNotif = async (e, id) => {
        e.stopPropagation();
        setNotifications((prev) => prev.filter((n) => n.id !== id));
        setNotificationIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        try {
            await api.delete(`/notifications/${id}`);
        } catch {
            fetchNotifications(true);
        }
    };

    // ---------- Status helpers ----------
    const statusPill = useMemo(() => {
        if (wsConnected === null) {
            return {
                dot: 'bg-yellow-500 animate-pulse',
                text: 'Connecting',
                cls: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
            };
        }
        if (wsConnected) {
            return {
                dot: 'bg-green-500 animate-pulse',
                text: 'Live',
                cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
            };
        }
        if (isRateLimited) {
            return {
                dot: 'bg-red-500',
                text: 'Rate Limited',
                cls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
            };
        }
        if (reconnectAttempts > 0 && reconnectAttempts < WS_LIMITS.MAX_RECONNECT) {
            return {
                dot: 'bg-amber-500 animate-pulse',
                text: `Reconnecting (${reconnectAttempts}/${WS_LIMITS.MAX_RECONNECT})`,
                cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
            };
        }
        return {
            dot: 'bg-gray-400',
            text: 'Offline',
            cls: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
        };
    }, [wsConnected, isRateLimited, reconnectAttempts]);

    // =========================================================
    // RENDER
    // =========================================================
    return (
        <header className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-lg shadow-sm border-b border-gray-200 dark:border-slate-700 sticky top-0 z-30">
            <div className="max-w-full mx-auto px-4 sm:px-6">
                <div className="flex justify-between items-center h-16 md:h-20">

                    {/* ============ LEFT ============ */}
                    <div className="flex items-center gap-3 min-w-0">
                        {isMobile && (
                            <button
                                onClick={onToggleSidebar}
                                aria-label="Toggle sidebar"
                                className="p-2 text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        )}

                        <Link to="/dashboard" className="flex items-center gap-2 group min-w-0">
                            <img
                                src={mayfairLogo}
                                alt="Mayfair Insurance"
                                className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
                            />
                            <span className="text-sm font-bold text-brand-600 dark:text-green-400 hidden sm:block truncate">
                                Mayfair Insurance
                            </span>
                        </Link>

                        {/* Connection pill */}
                        <div className="hidden lg:flex items-center ml-3 pl-3 border-l border-gray-200 dark:border-slate-700">
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusPill.cls}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${statusPill.dot}`} />
                                <span>{statusPill.text}</span>
                            </div>
                        </div>

                        {/* Greeting */}
                        {!isMobile && (
                            <div className="hidden xl:flex items-center gap-2 ml-3 min-w-0">
                                <span className="text-xl flex-shrink-0">{greeting.emoji}</span>
                                <span className="text-sm text-gray-600 dark:text-slate-300 truncate">
                                    {greeting.text},{' '}
                                    <strong className="text-gray-800 dark:text-white">
                                        {user?.full_name?.split(' ')[0] || 'User'}
                                    </strong>
                                </span>
                            </div>
                        )}
                    </div>

                    {/* ============ RIGHT ============ */}
                    <div className="flex items-center gap-1 md:gap-2">

                        <SearchPopover
                            open={searchOpen}
                            setOpen={setSearchOpen}
                            refEl={searchRef}
                            navigate={navigate}
                        />

                        {/* Clock */}
                        <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-slate-700/50 rounded-xl border border-gray-200 dark:border-slate-600">
                            <Clock className="w-3.5 h-3.5 text-brand-600 dark:text-green-400" />
                            <span className="text-xs font-mono font-medium text-gray-600 dark:text-slate-300 tabular-nums">{clock}</span>
                        </div>

                        {/* Quick actions */}
                        <div className="hidden md:flex items-center">
                            <IconAction to="/quotes/new" icon={<Plus className="w-5 h-5" />} title="New Quote" />
                            <IconAction to="/cargo" icon={<FileText className="w-5 h-5" />} title="Cargo Certificate" />
                            <IconAction to="/dashboard" icon={<Home className="w-5 h-5" />} title="Dashboard" />
                        </div>

                        {/* Notifications */}
                        <NotificationsBell
                            refEl={notifRef}
                            open={notifOpen}
                            setOpen={setNotifOpen}
                            loading={loading}
                            notifications={displayNotifications}
                            grouped={grouped}
                            totalCount={notifications.length}
                            unreadCount={unreadCount}
                            wsConnected={wsConnected}
                            isRateLimited={isRateLimited}
                            error={error}
                            notificationSound={notificationSound}
                            toggleSound={() => setNotificationSound((s) => !s)}
                            onMarkAllRead={markAllAsRead}
                            onItemClick={handleNotifClick}
                            onItemDismiss={handleDismissNotif}
                            navigate={navigate}
                        />

                        {/* Profile */}
                        <ProfileMenu
                            refEl={profileRef}
                            open={profileOpen}
                            setOpen={setProfileOpen}
                            user={user}
                            initials={initials}
                            onLogout={handleLogout}
                        />
                    </div>
                </div>
            </div>
        </header>
    );
}

// =========================================================
// SUB-COMPONENTS
// =========================================================

function IconAction({ to, icon, title }) {
    return (
        <Link
            to={to}
            title={title}
            aria-label={title}
            className="p-2 text-gray-400 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        >
            {icon}
        </Link>
    );
}

function SearchPopover({ open, setOpen, refEl, navigate }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => inputRef.current?.focus(), 50);
        } else {
            setQuery('');
            setResults([]);
        }
    }, [open]);

    useEffect(() => {
        if (!open || query.trim().length < 2) {
            setResults([]);
            return;
        }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/search?q=${encodeURIComponent(query.trim())}`);
                setResults(data?.results || []);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(t);
    }, [query, open]);

    const RECENT = ['Q-20260714', 'MV Ever Given', 'POL-20260713'];

    return (
        <div className="relative hidden md:block" ref={refEl}>
            <button
                onClick={() => setOpen(!open)}
                aria-label="Search"
                className="p-2 text-gray-400 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            >
                <Search className="w-5 h-5" />
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 p-3 animate-fadeIn z-50">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search quotes, policies, vessels..."
                            className="w-full pl-10 pr-16 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all text-sm"
                        />
                        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-slate-600 text-gray-500 dark:text-slate-300 rounded font-mono">
                            ESC
                        </kbd>
                    </div>

                    {loading && (
                        <p className="text-xs text-gray-400 dark:text-slate-500 text-center py-4">Searching...</p>
                    )}

                    {!loading && results.length > 0 && (
                        <div className="mt-3 space-y-1 max-h-72 overflow-y-auto">
                            {results.map((r, i) => (
                                <button
                                    key={i}
                                    onClick={() => { navigate(r.link); setOpen(false); }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <span>{r.icon || '🔍'}</span>
                                    <span className="text-gray-700 dark:text-slate-200 truncate flex-1">{r.label}</span>
                                    <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-slate-600" />
                                </button>
                            ))}
                        </div>
                    )}

                    {!loading && query.length < 2 && (
                        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                            <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500 mb-2">Recent</p>
                            <div className="space-y-1">
                                {RECENT.map((item) => (
                                    <button
                                        key={item}
                                        onClick={() => setQuery(item)}
                                        className="w-full text-left px-3 py-1.5 text-sm text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        {item}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function NotificationsBell({
    refEl, open, setOpen, loading, notifications, grouped, totalCount,
    unreadCount, wsConnected, isRateLimited, error,
    notificationSound, toggleSound,
    onMarkAllRead, onItemClick, onItemDismiss, navigate,
}) {
    return (
        <div className="relative" ref={refEl}>
            <button
                onClick={() => setOpen(!open)}
                aria-label="Notifications"
                className="relative p-2 text-gray-500 hover:text-brand-600 dark:text-slate-400 dark:hover:text-green-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            >
                {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                ) : unreadCount > 0 ? (
                    <BellRing className="w-5 h-5" />
                ) : (
                    <Bell className="w-5 h-5" />
                )}

                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1.5 ring-2 ring-white dark:ring-slate-800">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}

                {wsConnected === true && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-green-500 animate-pulse ring-2 ring-white dark:ring-slate-800" />
                )}
                {wsConnected === null && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full bg-yellow-500 animate-pulse ring-2 ring-white dark:ring-slate-800" />
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 py-3 animate-fadeIn z-50 max-h-[80vh] overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center px-4 pb-3 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <Bell className="w-4 h-4 text-brand-600 dark:text-green-400" />
                            <h4 className="text-sm font-semibold text-gray-800 dark:text-white">Notifications</h4>
                            {wsConnected === true && (
                                <span className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                    <span className="text-[10px] text-green-500 font-medium">Live</span>
                                </span>
                            )}
                            {wsConnected === null && (
                                <span className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 animate-pulse" />
                                    <span className="text-[10px] text-yellow-500 font-medium">Connecting</span>
                                </span>
                            )}
                            {isRateLimited && (
                                <span className="text-[10px] text-red-500 font-medium">Rate limited</span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={toggleSound}
                                title={notificationSound ? 'Sound on' : 'Sound off'}
                                aria-label={notificationSound ? 'Mute notifications' : 'Unmute notifications'}
                                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                            >
                                {notificationSound ? <BellRing className="w-3.5 h-3.5" /> : <BellOff className="w-3.5 h-3.5" />}
                            </button>
                            {unreadCount > 0 && (
                                <button
                                    onClick={onMarkAllRead}
                                    className="text-xs text-brand-600 dark:text-green-400 font-medium hover:text-brand-700 dark:hover:text-green-300 transition-colors px-2 py-1 rounded-lg hover:bg-brand-500/10"
                                >
                                    Mark all read
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="overflow-y-auto flex-1 divide-y divide-gray-100 dark:divide-slate-700">
                        {loading ? (
                            <div className="px-4 py-8 text-center">
                                <Loader2 className="w-8 h-8 text-brand-600 animate-spin mx-auto" />
                                <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">Loading notifications...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="px-4 py-8 text-center">
                                <Bell className="w-8 h-8 text-gray-300 dark:text-slate-600 mx-auto" />
                                <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">No notifications</p>
                                <p className="text-xs text-gray-400 dark:text-slate-500">Stay tuned for updates</p>
                            </div>
                        ) : (
                            <>
                                {Object.entries(grouped).map(([label, items]) => (
                                    items.length > 0 && (
                                        <div key={label}>
                                            <p className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-slate-500 bg-gray-50/50 dark:bg-slate-900/30">
                                                {label}
                                            </p>
                                            {items.map((n) => (
                                                <NotificationItem
                                                    key={n.id}
                                                    n={n}
                                                    onClick={() => onItemClick(n)}
                                                    onDismiss={(e) => onItemDismiss(e, n.id)}
                                                    wsConnected={wsConnected}
                                                />
                                            ))}
                                        </div>
                                    )
                                ))}
                            </>
                        )}
                    </div>

                    {totalCount > 0 && (
                        <div className="px-4 pt-3 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                            <Link
                                to="/notifications"
                                onClick={() => setOpen(false)}
                                className="block w-full text-center text-xs text-brand-600 dark:text-green-400 font-medium hover:text-brand-700 dark:hover:text-green-300 transition-colors py-1 rounded-lg hover:bg-brand-500/10"
                            >
                                View all notifications
                            </Link>
                        </div>
                    )}

                    {error && (
                        <div className="px-4 pt-3 border-t border-gray-100 dark:border-slate-700 flex-shrink-0">
                            <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg">
                                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{error}</span>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function NotificationItem({ n, onClick, onDismiss, wsConnected }) {
    const style = getNotificationStyle(n.type);
    const Icon = style.Icon;
    const isRead = n.read || n.is_read;

    return (
        <div
            onClick={onClick}
            className={`group relative px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer ${!isRead ? 'bg-brand-500/5 dark:bg-brand-500/10' : ''
                }`}
        >
            <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full ${style.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${style.text}`} />
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{n.title}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                        <p className="text-[11px] text-gray-400 dark:text-slate-500">{formatRelativeTime(n.time)}</p>
                        {wsConnected === true && !isRead && (
                            <span className="text-[10px] text-green-500 font-medium">● Live</span>
                        )}
                    </div>
                </div>

                {!isRead && (
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-600 flex-shrink-0 mt-2" />
                )}

                <button
                    onClick={onDismiss}
                    aria-label="Dismiss"
                    className="opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2 p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700"
                >
                    <X className="w-3 h-3" />
                </button>
            </div>
        </div>
    );
}

function ProfileMenu({ refEl, open, setOpen, user, initials, onLogout }) {
    const [counts, setCounts] = useState({ quotes: null, policies: null });

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const [q, p] = await Promise.all([
                    api.get('/quotes?limit=1'),
                    api.get('/policies?limit=1'),
                ]);
                if (cancelled) return;
                setCounts({
                    quotes: q.data?.total ?? q.data?.length ?? 0,
                    policies: p.data?.total ?? p.data?.length ?? 0,
                });
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [open]);

    return (
        <div className="relative" ref={refEl}>
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors group focus:outline-none focus:ring-2 focus:ring-brand-500/40"
            >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-800 to-brand-600 flex items-center justify-center text-white font-semibold text-sm shadow-md ring-2 ring-brand-500/20 dark:ring-green-400/30 group-hover:ring-brand-500/40 transition-all">
                    {initials}
                </div>
                <div className="text-left hidden lg:block">
                    <p className="text-sm font-medium text-gray-800 dark:text-white leading-tight">{user?.full_name}</p>
                    <p className="text-xs text-gray-500 dark:text-slate-400 capitalize flex items-center gap-1">
                        <UserCheck className="w-3 h-3" />
                        {user?.role?.replace(/_/g, ' ')}
                    </p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-slate-700 py-2 animate-fadeIn z-50">
                    <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-800 to-brand-600 flex items-center justify-center text-white font-bold text-lg">
                                {initials}
                            </div>
                            <div className="min-w-0">
                                <p className="text-sm font-medium text-gray-800 dark:text-white truncate">{user?.full_name}</p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1 truncate">
                                    <Mail className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate">{user?.email}</span>
                                </p>
                                <span className="inline-block mt-1 px-2 py-0.5 bg-brand-500/10 dark:bg-brand-500/20 text-brand-600 dark:text-green-400 text-[10px] font-medium rounded-full capitalize">
                                    {user?.role?.replace(/_/g, ' ')}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="py-1">
                        <ProfileMenuItem to="/profile" icon={<User className="w-4 h-4" />} label="My Profile" setOpen={setOpen} />
                        <ProfileMenuItem to="/settings" icon={<Settings className="w-4 h-4" />} label="Settings" setOpen={setOpen} />
                        <ProfileMenuItem to="/help" icon={<HelpCircle className="w-4 h-4" />} label="Help & Support" setOpen={setOpen} />
                    </div>

                    <div className="border-t border-gray-100 dark:border-slate-700 mx-4" />

                    <div className="px-4 py-2">
                        <div className="grid grid-cols-2 gap-2">
                            <div className="text-center p-2 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">Quotes</p>
                                <p className="text-sm font-bold text-gray-800 dark:text-white tabular-nums">
                                    {counts.quotes === null ? '—' : counts.quotes}
                                </p>
                            </div>
                            <div className="text-center p-2 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                                <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-slate-500">Policies</p>
                                <p className="text-sm font-bold text-gray-800 dark:text-white tabular-nums">
                                    {counts.policies === null ? '—' : counts.policies}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-gray-100 dark:border-slate-700 pt-1">
                        <button
                            onClick={onLogout}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/40 focus:ring-inset"
                        >
                            <LogOut className="w-4 h-4" />
                            Sign Out
                            <ChevronRight className="w-3.5 h-3.5 text-red-300 dark:text-red-600 ml-auto" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function ProfileMenuItem({ to, icon, label, setOpen }) {
    return (
        <Link
            to={to}
            onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:ring-inset"
        >
            <span className="text-gray-400 dark:text-slate-500">{icon}</span>
            {label}
            <ChevronRight className="w-3.5 h-3.5 text-gray-300 dark:text-slate-600 ml-auto" />
        </Link>
    );
}